import React from "react";
import {
Alert,
Box,
Button,
Chip,
CircularProgress,
Dialog,
DialogActions,
DialogContent,
DialogTitle,
Divider,
MenuItem,
Paper,
Stack,
TextField,
Typography,
useMediaQuery,
} from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import { useSearchParams } from "react-router-dom";
import { restaurantTablesApi } from "../../../api/tables";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const SHAPES = [
["round", "Redonda"],
["square", "Cuadrada"],
["rectangle", "Rectangular"],
];
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function normalizeTable(table, index, canvas) {
const layout = table.layout || {};
const width = clamp(Number(layout.width || 96), 48, Math.min(600, canvas.width));
const height = clamp(Number(layout.height || 96), 48, Math.min(600, canvas.height));
const fallbackX = 36 + (index % 6) * 150;
const fallbackY = 36 + Math.floor(index / 6) * 140;
return {
  ...table,
  layout: {
    x: clamp(Number(layout.x ?? fallbackX), 0, Math.max(0, canvas.width - width)),
    y: clamp(Number(layout.y ?? fallbackY), 0, Math.max(0, canvas.height - height)),
    width,
    height,
    shape: ["round", "square", "rectangle"].includes(layout.shape) ? layout.shape : "round",
  },
};
}

function shapeRadius(shape) {
if (shape === "round") return "50%";
if (shape === "square") return 16;
return 18;
}

export default function RestaurantTableLayoutEditor({ projectId, canManage = true }) {
const mobile = useMediaQuery("(max-width:599.95px)", { noSsr: true });
const [searchParams, setSearchParams] = useSearchParams();
const [layout, setLayout] = React.useState(null);
const [sector, setSector] = React.useState("");
const [createOpen, setCreateOpen] = React.useState(false);
const [createBusy, setCreateBusy] = React.useState(false);
const [createError, setCreateError] = React.useState("");
const [createForm, setCreateForm] = React.useState({ identifier: "", name: "", capacity: "4", sector: "" });
const createActionHandledRef = React.useRef("");
const [selectedId, setSelectedId] = React.useState("");
const [loading, setLoading] = React.useState(true);
const [saving, setSaving] = React.useState(false);
const [dirty, setDirty] = React.useState(false);
const [error, setError] = React.useState("");
const [message, setMessage] = React.useState("");
const canvasRef = React.useRef(null);
const dragRef = React.useRef(null);

const load = React.useCallback(async (nextSector = "") => {
  try {
    setLoading(true);
    setError("");
    setMessage("");
    const data = await restaurantTablesApi.getLayout(projectId, nextSector);
    const canvas = data.canvas || { width: 1200, height: 760, gridSize: 20 };
    setLayout({ ...data, canvas, tables: (data.tables || []).map((table, index) => normalizeTable(table, index, canvas)) });
    setSector(data.sector || "");
    setSelectedId("");
    setDirty(false);
  } catch (err) {
    setError(err.message || "No se pudo cargar el plano de mesas.");
  } finally {
    setLoading(false);
  }
}, [projectId]);

React.useEffect(() => { load(""); }, [load]);

const openCreateDialog = React.useCallback(() => {
  if (!canManage) return;
  setCreateError("");
  setCreateForm({ identifier: "", name: "", capacity: "4", sector: sector || "" });
  setCreateOpen(true);
}, [canManage, sector]);

React.useEffect(() => {
  const action = String(searchParams.get("accion") || "").trim().toLowerCase();
  if (action !== "crear-mesa" || !canManage) return;
  const token = `${projectId}:${searchParams.toString()}`;
  if (createActionHandledRef.current === token) return;
  createActionHandledRef.current = token;
  openCreateDialog();
  const next = new URLSearchParams(searchParams);
  next.delete("accion");
  setSearchParams(next, { replace: true });
}, [canManage, openCreateDialog, projectId, searchParams, setSearchParams]);

const submitCreate = async (event) => {
  event.preventDefault();
  if (!canManage || createBusy) return;
  const identifier = String(createForm.identifier || "").trim();
  const capacity = Number(createForm.capacity);
  if (!identifier) { setCreateError("Indicá el número o identificador de la mesa."); return; }
  if (!Number.isInteger(capacity) || capacity < 1 || capacity > 1000) { setCreateError("La capacidad debe ser un entero entre 1 y 1000."); return; }
  try {
    setCreateBusy(true);
    setCreateError("");
    const created = await restaurantTablesApi.create(projectId, {
      identifier,
      name: String(createForm.name || "").trim(),
      capacity,
      sector: String(createForm.sector || "").trim(),
    });
    const targetSector = created?.sector || "";
    setCreateOpen(false);
    await load(targetSector);
    if (created?.id) setSelectedId(created.id);
    setMessage(`Mesa ${created?.identifier || identifier} creada.`);
  } catch (err) {
    setCreateError(err.message || "No se pudo crear la mesa.");
  } finally {
    setCreateBusy(false);
  }
};

const updateTable = React.useCallback((id, updater) => {
  if (!canManage) return;
  setLayout((current) => current ? {
    ...current,
    tables: current.tables.map((table) => table.id === id ? updater(table) : table),
  } : current);
  setDirty(true);
  setMessage("");
}, [canManage]);

const handlePointerDown = (event, table) => {
  if (!canManage || mobile || !canvasRef.current || saving) return;
  event.preventDefault();
  event.currentTarget.setPointerCapture?.(event.pointerId);
  const rect = canvasRef.current.getBoundingClientRect();
  dragRef.current = {
    id: table.id,
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    tableX: table.layout.x,
    tableY: table.layout.y,
    scaleX: layout.canvas.width / rect.width,
    scaleY: layout.canvas.height / rect.height,
  };
  setSelectedId(table.id);
};

const handlePointerMove = (event) => {
  const drag = dragRef.current;
  if (!drag || drag.pointerId !== event.pointerId) return;
  const table = layout?.tables.find((item) => item.id === drag.id);
  if (!table) return;
  const x = clamp(drag.tableX + (event.clientX - drag.startX) * drag.scaleX, 0, layout.canvas.width - table.layout.width);
  const y = clamp(drag.tableY + (event.clientY - drag.startY) * drag.scaleY, 0, layout.canvas.height - table.layout.height);
  updateTable(drag.id, (item) => ({ ...item, layout: { ...item.layout, x: Math.round(x), y: Math.round(y) } }));
};

const stopDrag = (event) => {
  if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
};

const selected = layout?.tables.find((table) => table.id === selectedId) || null;

const changeSelected = (field, value) => {
  if (!selected) return;
  updateTable(selected.id, (table) => {
    const next = { ...table.layout, [field]: value };
    next.width = clamp(Number(next.width || 96), 48, Math.min(600, layout.canvas.width));
    next.height = clamp(Number(next.height || 96), 48, Math.min(600, layout.canvas.height));
    next.x = clamp(Number(next.x || 0), 0, layout.canvas.width - next.width);
    next.y = clamp(Number(next.y || 0), 0, layout.canvas.height - next.height);
    return { ...table, layout: next };
  });
};

const save = async () => {
  if (!canManage || !layout || !dirty) return;
  try {
    setSaving(true);
    setError("");
    const saved = await restaurantTablesApi.saveLayout(projectId, {
      sector,
      canvas: layout.canvas,
      tables: layout.tables.map((table) => ({ id: table.id, ...table.layout })),
    });
    const canvas = saved.canvas || layout.canvas;
    setLayout({ ...saved, canvas, tables: (saved.tables || []).map((table, index) => normalizeTable(table, index, canvas)) });
    setDirty(false);
    setMessage("Distribución guardada.");
  } catch (err) {
    setError(err.message || "No se pudo guardar la distribución.");
  } finally {
    setSaving(false);
  }
};

if (loading && !layout) {
  return <Box sx={{ minHeight: 360, display: "grid", placeItems: "center" }}><CircularProgress /></Box>;
}

return (
  <Stack spacing={2.5}>
    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5} alignItems={{ md: "center" }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Distribución de mesas</Typography>
        <Typography variant="body2" color="text.secondary">{canManage ? (mobile ? "En celular seleccioná una mesa y ajustá su posición desde el panel." : "Arrastrá las mesas y guardá cuando termines.") : "Vista de solo lectura del plano de mesas."}</Typography>
      </Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap={{ sm: "wrap" }} useFlexGap sx={{ width: { xs: "100%", md: "auto" }, "& .MuiButton-root": { minHeight: 44 } }}>
        <TextField select size="small" label="Sector" value={sector} onChange={(event) => load(event.target.value)} sx={{ minWidth: { sm: 180 }, width: { xs: "100%", sm: "auto" } }} disabled={loading || saving}>
          {(layout?.sectors || [""]).map((item) => <MenuItem key={item || "__default__"} value={item}>{item || "Sin sector"}</MenuItem>)}
        </TextField>
        {canManage ? (
          <>
            <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={openCreateDialog} disabled={loading || saving} sx={{ textTransform: "none", fontWeight: 900, borderRadius: 999 }}>
              Agregar mesa
            </Button>
            <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={save} disabled={!dirty || saving} sx={{ bgcolor: BRAND.orange, textTransform: "none", fontWeight: 900, borderRadius: 999 }}>
              {saving ? "Guardando…" : "Guardar plano"}
            </Button>
          </>
        ) : null}
      </Stack>
    </Stack>

    {error ? <Alert severity="error">{error}</Alert> : null}
    {message ? <Alert severity="success">{message}</Alert> : null}

    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1fr) 280px" }, gap: 2 }}>
      {mobile ? (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 1 }}>
          {(layout?.tables || []).map((table) => {
            const selectedNow = selectedId === table.id;
            return (
              <Paper
                key={table.id}
                component="button"
                type="button"
                variant="outlined"
                onClick={() => setSelectedId(table.id)}
                sx={{
                  minHeight: 96,
                  p: 1.25,
                  borderRadius: 2.5,
                  border: selectedNow ? `3px solid ${BRAND.orange}` : "1px solid rgba(25,36,47,.12)",
                  bgcolor: selectedNow ? "#FFF2EE" : "#fff",
                  color: "inherit",
                  textAlign: "left",
                  font: "inherit",
                  cursor: "pointer",
                }}
              >
                <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Mesa {table.identifier}</Typography>
                <Typography variant="body2" color="text.secondary">{table.capacity} lugares{table.name ? ` · ${table.name}` : ""}</Typography>
                <Typography variant="caption" color="text.secondary">X {Math.round(table.layout.x)} · Y {Math.round(table.layout.y)}</Typography>
              </Paper>
            );
          })}
          {!layout?.tables?.length ? <Alert severity="info" sx={{ gridColumn: "1 / -1" }}>No hay mesas activas en este sector.</Alert> : null}
        </Box>
      ) : (
        <Paper elevation={0} sx={{ p: 1.5, border: "1px solid rgba(25,36,47,.10)", borderRadius: 3, overflow: "hidden" }}>
          <Box
            ref={canvasRef}
            onPointerMove={handlePointerMove}
            onPointerUp={stopDrag}
            onPointerCancel={stopDrag}
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: `${layout?.canvas.width || 1200} / ${layout?.canvas.height || 760}`,
              minHeight: 420,
              bgcolor: "#FAFBFC",
              borderRadius: 2,
              overflow: "hidden",
              backgroundImage: "linear-gradient(rgba(25,36,47,.055) 1px, transparent 1px), linear-gradient(90deg, rgba(25,36,47,.055) 1px, transparent 1px)",
              backgroundSize: "20px 20px",
              touchAction: "none",
            }}
          >
            {(layout?.tables || []).map((table) => {
              const item = table.layout;
              const selectedNow = selectedId === table.id;
              return (
                <Box
                  key={table.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Mesa ${table.identifier}, capacidad ${table.capacity}`}
                  onPointerDown={(event) => handlePointerDown(event, table)}
                  onClick={() => setSelectedId(table.id)}
                  sx={{
                    position: "absolute",
                    left: `${(item.x / layout.canvas.width) * 100}%`,
                    top: `${(item.y / layout.canvas.height) * 100}%`,
                    width: `${(item.width / layout.canvas.width) * 100}%`,
                    height: `${(item.height / layout.canvas.height) * 100}%`,
                    minWidth: 48,
                    minHeight: 48,
                    borderRadius: shapeRadius(item.shape),
                    border: selectedNow ? `3px solid ${BRAND.orange}` : "2px solid rgba(25,36,47,.38)",
                    bgcolor: selectedNow ? "#FFF2EE" : "white",
                    boxShadow: selectedNow ? "0 8px 22px rgba(243,90,58,.18)" : "0 4px 12px rgba(25,36,47,.08)",
                    display: "grid",
                    placeItems: "center",
                    textAlign: "center",
                    userSelect: "none",
                    cursor: canManage && !saving ? "grab" : "pointer",
                    touchAction: canManage ? "none" : "auto",
                    px: .5,
                  }}
                >
                  <Box sx={{ pointerEvents: "none", lineHeight: 1.05 }}>
                    <Typography sx={{ fontWeight: 950, color: BRAND.dark, fontSize: { sm: 13, md: 14 } }}>{table.identifier}</Typography>
                    <Typography variant="caption" sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: .35, color: "text.secondary" }}>
                      <PeopleAltRoundedIcon sx={{ fontSize: 13 }} /> {table.capacity}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
            {!layout?.tables?.length ? <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}><Typography color="text.secondary">No hay mesas activas en este sector.</Typography></Box> : null}
          </Box>
        </Paper>
      )}

      <Paper elevation={0} sx={{ p: 2, border: "1px solid rgba(25,36,47,.10)", borderRadius: 3, alignSelf: "start" }}>
        <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Mesa seleccionada</Typography>
        <Divider sx={{ my: 1.5 }} />
        {!selected ? <Typography variant="body2" color="text.secondary">Tocá una mesa para {canManage ? "editarla" : "verla"}.</Typography> : (
          <Stack spacing={1.5}>
            <Box><Typography variant="h6" sx={{ fontWeight: 950 }}>{selected.identifier}</Typography><Typography variant="body2" color="text.secondary">{selected.name || "Sin nombre"}</Typography></Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap><Chip size="small" label={`${selected.capacity} personas`} /><Chip size="small" variant="outlined" label={selected.operationalStatus || "available"} /></Stack>
            <TextField select size="small" label="Forma" value={selected.layout.shape} disabled={!canManage} onChange={(event) => changeSelected("shape", event.target.value)}>{SHAPES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</TextField>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
              <TextField size="small" type="number" label="Ancho" value={Math.round(selected.layout.width)} disabled={!canManage} onChange={(event) => changeSelected("width", Number(event.target.value))} inputProps={{ min: 48, max: 600 }} fullWidth />
              <TextField size="small" type="number" label="Alto" value={Math.round(selected.layout.height)} disabled={!canManage} onChange={(event) => changeSelected("height", Number(event.target.value))} inputProps={{ min: 48, max: 600 }} fullWidth />
              {mobile ? (<>
                <TextField size="small" type="number" label="Posición X" value={Math.round(selected.layout.x)} disabled={!canManage} onChange={(event) => changeSelected("x", Number(event.target.value))} inputProps={{ min: 0, max: Math.max(0, layout.canvas.width - selected.layout.width) }} fullWidth />
                <TextField size="small" type="number" label="Posición Y" value={Math.round(selected.layout.y)} disabled={!canManage} onChange={(event) => changeSelected("y", Number(event.target.value))} inputProps={{ min: 0, max: Math.max(0, layout.canvas.height - selected.layout.height) }} fullWidth />
              </>) : null}
            </Box>
            <Typography variant="caption" color="text.secondary">{canManage ? (mobile ? "La vista queda estable para evitar arrastres accidentales. En tablet o escritorio también podés mover mesas directamente." : "Arrastrá para mover; los campos conservan medidas exactas.") : "Tu rol permite consultar este plano, pero no modificarlo."}</Typography>
          </Stack>
        )}
      </Paper>
    </Box>

    <Dialog open={canManage && createOpen} onClose={() => createBusy ? null : setCreateOpen(false)} fullWidth maxWidth="sm" fullScreen={mobile}>
      <Box component="form" onSubmit={submitCreate}>
        <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark }}>Agregar mesa</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ pt: .5 }}>
            {createError ? <Alert severity="error">{createError}</Alert> : null}
            <TextField
              autoFocus
              required
              fullWidth
              label="Número o identificador"
              value={createForm.identifier}
              onChange={(event) => setCreateForm((current) => ({ ...current, identifier: event.target.value }))}
              inputProps={{ maxLength: 60 }}
              helperText="Ej.: 1, M01, Terraza 4."
            />
            <TextField
              fullWidth
              label="Nombre (opcional)"
              value={createForm.name}
              onChange={(event) => setCreateForm((current) => ({ ...current, name: event.target.value }))}
              inputProps={{ maxLength: 120 }}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
              <TextField
                required
                fullWidth
                type="number"
                label="Capacidad"
                value={createForm.capacity}
                onChange={(event) => setCreateForm((current) => ({ ...current, capacity: event.target.value }))}
                inputProps={{ min: 1, max: 1000, step: 1 }}
              />
              <TextField
                fullWidth
                label="Sector (opcional)"
                value={createForm.sector}
                onChange={(event) => setCreateForm((current) => ({ ...current, sector: event.target.value }))}
                inputProps={{ maxLength: 120 }}
                helperText="Ej.: Salón, Patio, Terraza."
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={createBusy}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={createBusy} startIcon={createBusy ? <CircularProgress size={17} /> : <AddRoundedIcon />} sx={{ bgcolor: BRAND.orange }}>
            Crear mesa
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  </Stack>
);
}
